import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApiService } from '@/lib/api';
import { useConversationCache } from '@/hooks/use-conversation-cache';
import type { ChatSummary } from '@/hooks/use-conversation-cache';
import { ConversationErrorBoundary } from '@/components/error-boundaries/conversation-error-boundary';
import { 
  MessageSquare, 
  Star,
  Loader2,
  GraduationCap,
  MoreHorizontal,
  Edit2,
  Trash2,
  Check,
  X,
  Plus,
  RefreshCw
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar';
import { cn } from '@/lib/utils';

interface ConversationHistoryProps {
  onChatSelect?: (chatId: string) => void;
}

export const ConversationHistory = React.memo(function ConversationHistory({ onChatSelect }: ConversationHistoryProps) {
  const [editingChatId, setEditingChatId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [updatingChat, setUpdatingChat] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  
  const apiService = useApiService();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Use conversation cache hook for optimized data management
  const {
    conversations,
    hasLoaded,
    currentPage,
    hasMore,
    isLoading,
    loadingMore,
    error: cacheError,
    fetchConversations,
    updateConversation,
    removeConversation,
    refreshConversations
  } = useConversationCache();

  // Combine cache error with local error
  const error = cacheError || localError;

  // Load conversations when component mounts - only if not cached
  useEffect(() => {
    if (!hasLoaded && !isLoading) {
      fetchConversations(1);
    }
  }, [hasLoaded, isLoading]); // Removed fetchConversations from deps to prevent re-fetching

  const handleChatClick = useCallback((chat: ChatSummary) => {
    // Check if this is a beacon chat
    if (chat.isBeacon) {
      // For beacon chats, navigate to beacon learn page with chat ID
      if (chat.entityId) {
        navigate(`/beacons/learn/${chat.entityId}/chat/${chat._id}`);
      } else {
        // Fallback to dashboard with chatId
        navigate(`/dashboard/chat/${chat._id}`);
      }
    } else {
      // Handle regular dashboard chats - navigate to /dashboard/chat/<chatId>
      navigate(`/dashboard/chat/${chat._id}`);
    }
  }, [navigate]);

  const loadMore = useCallback(() => {
    if (!loadingMore && hasMore) {
      fetchConversations(currentPage + 1, true);
    }
  }, [loadingMore, hasMore, currentPage]); // Removed fetchConversations from deps

  const handleRenameChat = async (chatId: string, newTitle: string) => {
    try {
      setUpdatingChat(chatId);
      await apiService.put(`/chats/${chatId}/title`, { title: newTitle });
      
      // Update the conversation using cache method
      updateConversation(chatId, { title: newTitle });
      
      setEditingChatId(null);
      setEditingTitle('');
    } catch (err) {
      console.error('Failed to rename chat:', err);
      setLocalError('Failed to rename chat');
    } finally {
      setUpdatingChat(null);
    }
  };

  const handleDeleteChat = async (chatId: string) => {
    try {
      setUpdatingChat(chatId);
      await apiService.delete(`/chats/${chatId}`);
      
      // Remove the chat using cache method
      removeConversation(chatId);
    } catch (err) {
      console.error('Failed to delete chat:', err);
      setLocalError('Failed to delete chat');
    } finally {
      setUpdatingChat(null);
    }
  };

  const handleFavoriteChat = async (chatId: string) => {
    try {
      setUpdatingChat(chatId);
      await apiService.get(`/chats/${chatId}/favorite`);
      
      // Find current chat to toggle favorite status
      const currentChat = conversations.find(chat => chat._id === chatId);
      if (currentChat) {
        updateConversation(chatId, { isFavorite: !currentChat.isFavorite });
      }
    } catch (err) {
      console.error('Failed to favorite chat:', err);
      setLocalError('Failed to favorite chat');
    } finally {
      setUpdatingChat(null);
    }
  };

  const startEditing = (chatId: string, currentTitle: string) => {
    setEditingChatId(chatId);
    setEditingTitle(currentTitle);
  };

  const cancelEditing = () => {
    setEditingChatId(null);
    setEditingTitle('');
  };

  const saveEditing = (chatId: string) => {
    if (editingTitle.trim() && editingTitle.trim() !== conversations.find(c => c._id === chatId)?.title) {
      handleRenameChat(chatId, editingTitle.trim());
    } else {
      cancelEditing();
    }
  };

  const getChatCategory = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0 || diffDays === 1) {
      return 'Recent';
    } else if (diffDays <= 7) {
      return 'Previous 7 days';
    } else if (diffDays <= 30) {
      return 'Previous 30 days';
    } else {
      // Return month name and year
      return date.toLocaleDateString('en-US', { 
        month: 'long', 
        year: 'numeric' 
      });
    }
  };

  const groupConversationsByDate = (conversations: ChatSummary[]) => {
    const groups: { [key: string]: ChatSummary[] } = {};
    
    conversations.forEach(chat => {
      const category = getChatCategory(chat.createdAt);
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(chat);
    });

    // Sort groups by priority (Recent, Previous 7 days, etc.)
    const sortOrder = ['Recent', 'Previous 7 days', 'Previous 30 days'];
    const sortedGroups: { [key: string]: ChatSummary[] } = {};
    
    // Add priority groups first
    sortOrder.forEach(key => {
      if (groups[key]) {
        sortedGroups[key] = groups[key];
      }
    });
    
    // Add month groups (sorted by date descending)
    Object.keys(groups)
      .filter(key => !sortOrder.includes(key))
      .sort((a, b) => {
        const dateA = new Date(a + ' 1'); // Add day to make it a valid date
        const dateB = new Date(b + ' 1');
        return dateB.getTime() - dateA.getTime();
      })
      .forEach(key => {
        sortedGroups[key] = groups[key];
      });
    
    return sortedGroups;
  };

  const getFavoriteChats = () => {
    return conversations.filter(chat => chat.isFavorite);
  };

  const getNonFavoriteChats = () => {
    return conversations.filter(chat => !chat.isFavorite);
  };

  const truncateTitle = (title: string, maxLength: number = 32) => {
    if (title.length <= maxLength) return title;
    return title.slice(0, maxLength).trim() + '...';
  };

  const ChatItem = ({ chat }: { chat: ChatSummary }) => {
    const isEditing = editingChatId === chat._id;
    const isUpdating = updatingChat === chat._id;

    const handleKeyPress = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        saveEditing(chat._id);
      } else if (e.key === 'Escape') {
        cancelEditing();
      }
    };

    return (
      <SidebarMenuItem key={chat._id}>
        <div className="flex items-center w-full group">
          {isEditing ? (
            <div className="flex items-center gap-1 w-full px-2 py-1.5">
              <div className="flex-shrink-0">
                {chat.isBeacon ? (
                  <div className="flex items-center justify-center w-4 h-4 rounded-md bg-gradient-to-br from-blue-500 to-purple-600 shadow-sm">
                    <GraduationCap className="h-2.5 w-2.5 text-white" />
                  </div>
                ) : (
                  <MessageSquare className="h-4 w-4 text-muted-foreground/60" />
                )}
              </div>
              <Input
                value={editingTitle}
                onChange={(e) => setEditingTitle(e.target.value)}
                onKeyDown={handleKeyPress}
                className="h-6 text-xs flex-1 min-w-0"
                autoFocus
                disabled={isUpdating}
              />
              <div className="flex items-center gap-1 flex-shrink-0">
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 w-6 p-0"
                  onClick={() => saveEditing(chat._id)}
                  disabled={isUpdating}
                >
                  <Check className="h-3 w-3" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 w-6 p-0"
                  onClick={cancelEditing}
                  disabled={isUpdating}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            </div>
          ) : (
            <>
              <SidebarMenuButton
                className={cn(
                  "h-auto py-1.5 text-left flex-1 min-w-0",
                  "hover:bg-accent/50 hover:text-accent-foreground transition-colors duration-150"
                )}
                onClick={() => handleChatClick(chat)}
                disabled={isUpdating}
              >
                <div className="flex items-center gap-2 w-full min-w-0">
                  <div className="flex-shrink-0">
                    {chat.isBeacon ? (
                      <div className="flex items-center justify-center w-4 h-4 rounded-md bg-gradient-to-br from-blue-500 to-purple-600 shadow-sm">
                        <GraduationCap className="h-2.5 w-2.5 text-white" />
                      </div>
                    ) : (
                      <MessageSquare className="h-4 w-4 text-muted-foreground/60" />
                    )}
                  </div>
                  
                  <div className="flex-1 min-w-0 flex items-center justify-between">
                    <div className="flex flex-col min-w-0">
                      <span className="text-xs text-foreground/90 truncate pr-1 leading-4">
                        {truncateTitle(chat.title)}
                      </span>
                      {chat.isBeacon && (
                        <span className="text-[10px] text-blue-600/70 font-medium">
                          Learning Session
                        </span>
                      )}
                    </div>
                    {chat.isFavorite && (
                      <Star className="h-3 w-3 text-yellow-500 fill-current flex-shrink-0" />
                    )}
                  </div>
                </div>
              </SidebarMenuButton>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                    disabled={isUpdating}
                  >
                    <MoreHorizontal className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-40">
                  <DropdownMenuItem
                    onClick={() => startEditing(chat._id, chat.title)}
                    disabled={isUpdating}
                  >
                    <Edit2 className="h-3 w-3 mr-2" />
                    Rename
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => handleFavoriteChat(chat._id)}
                    disabled={isUpdating}
                  >
                    <Star className={cn(
                      "h-3 w-3 mr-2",
                      chat.isFavorite ? "fill-current text-yellow-500" : ""
                    )} />
                    {chat.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => handleDeleteChat(chat._id)}
                    disabled={isUpdating}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="h-3 w-3 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}
        </div>
      </SidebarMenuItem>
    );
  };

  return (
    <ConversationErrorBoundary>
      <SidebarGroup>
        <SidebarGroupLabel>
          <div className="flex items-center justify-between w-full">
            <span>Chats</span>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="h-4 w-4 p-0 hover:bg-sidebar-accent"
                onClick={async () => {
                  try {
                    await refreshConversations();
                  } catch (error) {
                    console.error('❌ Manual refresh failed:', error);
                  }
                }}
                title="Refresh conversations"
              >
                <RefreshCw className="h-3 w-3" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-4 w-4 p-0 hover:bg-sidebar-accent"
                onClick={() => navigate('/dashboard')}
                title="New chat"
              >
                <Plus className="h-3 w-3" />
              </Button>
            </div>
          </div>
        </SidebarGroupLabel>
        
        {isLoading ? (
          <div className="flex items-center justify-center py-3">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="ml-2 text-xs text-muted-foreground">Loading...</span>
          </div>
        ) : error ? (
          <div className="px-2 py-2 text-xs text-destructive">
            {error}
          </div>
        ) : conversations.length === 0 ? (
          <div className="px-2 py-3 text-xs text-muted-foreground">
            No conversations yet
          </div>
        ) : (
          <>
            <div className="space-y-2">
              {/* Favorites Section */}
              {getFavoriteChats().length > 0 && (
                <div className="space-y-0.5">
                  <div className="px-2 py-0.5">
                    <h4 className="text-[10px] font-semibold text-muted-foreground/80 uppercase tracking-wide">
                      Favorites
                    </h4>
                  </div>
                  <SidebarMenu>
                    {getFavoriteChats().map((chat) => (
                      <ChatItem key={chat._id} chat={chat} />
                    ))}
                  </SidebarMenu>
                </div>
              )}
              
              {/* Regular Chat History */}
              {Object.entries(groupConversationsByDate(getNonFavoriteChats())).map(([category, chats]) => (
                <div key={category} className="space-y-0.5">
                  <div className="px-2 py-0.5">
                    <h4 className="text-[10px] font-semibold text-muted-foreground/80 uppercase tracking-wide">
                      {category}
                    </h4>
                  </div>
                  <SidebarMenu>
                    {chats.map((chat) => (
                      <ChatItem key={chat._id} chat={chat} />
                    ))}
                  </SidebarMenu>
                </div>
              ))}
            </div>
            
            {hasMore && (
              <div className="pt-2 border-t">
                <SidebarMenu>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      className="h-7 text-xs text-muted-foreground hover:text-foreground justify-center"
                      onClick={loadMore}
                      disabled={loadingMore}
                    >
                      {loadingMore ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                          Loading...
                        </>
                      ) : (
                        'Show more'
                      )}
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </div>
            )}
          </>
        )}
      </SidebarGroup>
    </ConversationErrorBoundary>
  );
});
